import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import { selector } from '../../../StockFilters/selectors';
import {
  StockListQuery,
  StockListQueryVariables,
  StockListQuery_stocksRemote_products_page as Page,
} from './__generated__/StockListQuery';
import * as query from './query.gql';
import { SortableFields } from 'components/StockFilters/actions';

export const PAGE_SIZE = 20;

const useFilter = (): StockListQueryVariables['filter'] => {
  const {
    actualName,
    actualCapRange,
    currency,
    actualRisk,
    actualType,
    actualSector,
    actualIndustry,
  } = useSelector(selector);

  if (actualName) {
    return {
      searchString: actualName,
    };
  } else {
    const capitalizationFrom =
      actualCapRange && actualCapRange.min ? { value: actualCapRange.min, currency } : null;

    const capitalizationTo =
      actualCapRange && actualCapRange.max && actualCapRange.max !== Infinity
        ? { value: actualCapRange.max, currency }
        : null;

    return {
      risk: actualRisk,
      type: actualType,
      sector: actualSector,
      industry: actualIndustry,
      capitalizationFrom,
      capitalizationTo,
    };
  }
};

const usePagination = (): StockListQueryVariables['pagination'] => {
  const { sortDirection, sortedField } = useSelector(selector);

  return {
    page: 0,
    size: PAGE_SIZE,
    sort: sortedField
      ? {
          property: fieldToSortProperty(sortedField),
          direction: sortDirection,
        }
      : undefined,
  };
};

const fieldToSortProperty = (field: SortableFields) => {
  if (field === 'priceChange') {
    return 'priceDiff';
  }
  return field;
};

const getPortion = (page: Page) => {
  if (page && page.totalElements) {
    const compensatedPageNumber = page.number + 1;
    return Math.min(PAGE_SIZE, page.totalElements - compensatedPageNumber * PAGE_SIZE);
  }

  return PAGE_SIZE;
};

export const useList = () => {
  const { sortDirection, sortedField } = useSelector(selector);

  const filter = useFilter();

  const pagination = usePagination();

  const { data, loading, fetchMore } = useQuery<StockListQuery, StockListQueryVariables>(query, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filter,
      pagination,
    },
  });

  if (!data?.stocksRemote) {
    return {
      loading: true,
    };
  }

  const { list, page } = data?.stocksRemote.products;

  return {
    stocks: list,
    page,
    loading,
    fetchMore: () =>
      fetchMore({
        variables: {
          pagination: {
            size: PAGE_SIZE,
            page: page.number + 1,
            sort: sortedField
              ? {
                  property: fieldToSortProperty(sortedField),
                  direction: sortDirection,
                }
              : undefined,
          },
        },
        updateQuery(prev, { fetchMoreResult }) {
          if (!fetchMoreResult) {
            return prev;
          }

          return {
            ...prev,
            stocksRemote: {
              ...prev.stocksRemote,
              products: {
                ...prev.stocksRemote.products,
                page: fetchMoreResult.stocksRemote.products.page,
                list: [
                  ...prev.stocksRemote.products.list,
                  ...fetchMoreResult.stocksRemote.products.list,
                ],
              },
            },
          };
        },
      }),
    nextPortion: getPortion(page),
    totalElements: page.totalElements,
    hasMore: page.hasMore,
  };
};
